import { useEffect, useState } from 'react'

import { Button } from '@repo/ui'
import { useSnackbar } from 'contexts'

import { NarrationRespendModal } from './NarrationRespendModal'
import { planRespend, realProfile, type NarrationInput, type RespendPlan } from './narrationRespend'

/**
 * VRG-739 — Labs specimen for the re-spend gate, until an article editor exists to open it.
 *
 * The plan is COMPUTED (planRespend, WebCrypto in the browser) from a small sample article and an
 * edit to three of its five sections, so the figures on screen are the function's, not typed in.
 * Every button only closes the modal and says so: the demo sends nothing anywhere.
 */
const para = (text: string) => ({ type: 'paragraph' as const, text })
const NARRATED: NarrationInput = {
  title: 'What a passport carries',
  lead: 'A short sample article for the Labs gallery.',
  blocks: [
    { type: 'heading', text: 'Identity' },
    para('Every item gets a record that follows it from the factory to every later owner.'.repeat(6)),
    { type: 'heading', text: 'Materials' },
    para('The record names what the item is made of and where those materials came from.'.repeat(5)),
    { type: 'heading', text: 'Repairs' },
    para('Each repair is added to the record, so the next owner knows what was done.'.repeat(4)),
    { type: 'heading', text: 'Resale' },
    para('When the item resells, the record moves to the new owner with it.'.repeat(7)),
  ],
}
const EDITED: NarrationInput = {
  ...NARRATED,
  title: 'What a product passport carries',
  blocks: NARRATED.blocks.map((b, i) =>
    b.type === 'paragraph' && (i === 3 || i === 7) ? { ...b, text: `${b.text} This sentence is new.` } : b,
  ),
}
const PROFILE = realProfile({ voice: 'demo-voice' })

export const NarrationRespendDemo = () => {
  const { open: notify } = useSnackbar()
  const [plan, setPlan] = useState<RespendPlan | null>(null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    planRespend({ input: NARRATED, profile: PROFILE }, { input: EDITED, profile: PROFILE }).then(setPlan, (e: Error) =>
      setError(e.message),
    )
  }, [])

  const close = (text: string) => {
    setOpen(false)
    notify({ type: 'info', text })
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} disabled={!plan}>
        {error ?? 'Save a narrated article'}
      </Button>
      <NarrationRespendModal
        open={open}
        articleTitle={EDITED.title}
        plan={plan}
        onRenarrate={() => close('Demo: would save and queue re-narration. Nothing was sent.')}
        onSaveWithoutRenarrating={() => close('Demo: would save; narration marked stale. Nothing was sent.')}
        onCancel={() => setOpen(false)}
      />
    </>
  )
}
